/**
 * features/applications/ApplicationsList.tsx
 * ------------------------------------------------------------------
 * Internal list of submitted applications, shown as a tab inside the
 * Admissions module. Gated by canReviewApplications.
 *
 * Lists every doc in `reportApplications` with filters by status and
 * by applying-for form. Clicking a row opens ApplicationDetail.
 *
 * Status pills:
 *   pending       — newly submitted, untouched
 *   under_review  — assigned to a reviewer / decisions in progress
 *   approved      — all decisions made, ready to admit
 *   rejected      — terminal (kept for the audit trail)
 *
 * Admitted applications are not shown here because admitApplication
 * deletes the doc on success (the new student lives in
 * reportStudents). Rejected ones remain visible with a filter so
 * the office can refer back to them.
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import {
  ClipboardList,
  Filter,
  Search,
  Clock,
  CheckCircle2,
  XCircle,
  Copy,
  HelpCircle,
  AlertCircle,
  ChevronRight,
  Calendar,
  GraduationCap,
  Users,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { Spinner, Button } from '@/components/ui/primitives';
import BulkAdmit from '@/features/applications/BulkAdmit';
import {
  GradientHero,
  SoftCard,
  StatChip,
} from '@/features/finance/ui';
import type {
  ApplicationRecord,
  ApplicationStatus,
} from '@/types';

const STATUS_META: Record<
  ApplicationStatus,
  { label: string; icon: typeof Clock; cls: string }
> = {
  pending: {
    label: 'New',
    icon: Clock,
    cls: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  under_review: {
    label: 'In review',
    icon: AlertCircle,
    cls: 'bg-sky-100 text-sky-700 border-sky-200',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle2,
    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  duplicate: {
    label: 'Duplicate',
    icon: Copy,
    cls: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    cls: 'bg-red-100 text-red-700 border-red-200',
  },
};

interface Props {
  onOpenApplication: (id: string) => void;
}

export default function ApplicationsList({ onOpenApplication }: Props) {
  const [apps, setApps] = useState<ApplicationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'all'>('all');
  const [formFilter, setFormFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showBulk, setShowBulk] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, 'reportApplications'),
      orderBy('submittedAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const out: ApplicationRecord[] = [];
        snap.forEach((d) => {
          const data = { id: d.id, ...d.data() } as ApplicationRecord;
          // Admitted applications are kept for the audit trail but hidden
          // here — they now live as a Student in reportStudents.
          if (data.admittedStudentId) return;
          out.push(data);
        });
        setApps(out);
        setLoading(false);
      },
      (err) => {
        console.error('Applications subscribe failed', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (formFilter !== 'all' && a.applyingForForm !== formFilter) return false;
      if (q) {
        const hay = [
          a.referenceNumber,
          a.firstName,
          a.surname,
          ...(a.guardians ?? []).map((g) => g.fullName),
          ...(a.guardians ?? []).map((g) => g.email ?? ''),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [apps, statusFilter, formFilter, search]);

  const counts = useMemo(() => {
    /* Keyed loosely on purpose: a status the client has not been taught
       about still gets counted rather than throwing. `total` is taken from
       the array, so the tallies can never sum to more than there are. */
    const c: Record<string, number> = {
      pending: 0, under_review: 0, approved: 0, rejected: 0, duplicate: 0,
      total: apps.length,
    };
    for (const a of apps) {
      const k = a.status ? String(a.status) : 'unknown';
      c[k] = (c[k] ?? 0) + 1;
    }
    return c;
  }, [apps]);

  const formOptions = useMemo(() => {
    const s = new Set<string>();
    for (const a of apps) s.add(a.applyingForForm);
    return Array.from(s).sort();
  }, [apps]);

  if (loading) return <Spinner label="Loading applications…" />;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={ClipboardList}
        title="Applications"
        subtitle="Submissions from the public application form"
        actions={
          counts.approved > 0 ? (
            <Button icon={<Users size={15} />} onClick={() => setShowBulk(true)}>
              Bulk admit ({counts.approved})
            </Button>
          ) : undefined
        }
      />

      {showBulk && <BulkAdmit applications={apps} onClose={() => setShowBulk(false)} />}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatChip label="Total" value={counts.total} icon={ClipboardList} />
        <StatChip label="New" value={counts.pending} icon={Clock} />
        <StatChip label="In review" value={counts.under_review} icon={AlertCircle} />
        <StatChip label="Approved" value={counts.approved} icon={CheckCircle2} />
        <StatChip label="Rejected" value={counts.rejected} icon={XCircle} />
      </div>

      <SoftCard>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-4">
          <div className="relative flex-1 min-w-0">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, reference, or email…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm focus:outline-none focus:border-primaryLight"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ApplicationStatus | 'all')}
              className="px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm"
            >
              <option value="all">All statuses</option>
              <option value="pending">New</option>
              <option value="under_review">In review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="duplicate">Duplicate</option>
            </select>
            <select
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
              className="px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm"
            >
              <option value="all">All forms</option>
              {formOptions.map((f) => (
                <option key={f} value={f}>
                  Form {f}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <ClipboardList size={32} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm">
              {apps.length === 0
                ? 'No applications yet. Share the application form link to start receiving submissions.'
                : 'No applications match the current filters.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700 -mx-4">
            {filtered.map((a) => {
              /* NEVER let one row blank the screen. A status the client does
                 not recognise used to return undefined here and throw inside
                 the map, so a single application took the whole admissions
                 list down — which is how a duplicate submission on 3 Sep 2026
                 left the office with an empty page and no explanation.
                 An unrecognised status is now shown AS ITSELF, so the row is
                 still readable and the gap is visible rather than fatal. */
              const meta = STATUS_META[a.status] ?? {
                label: a.status ? String(a.status) : 'Unknown',
                icon: HelpCircle,
                cls: 'bg-gray-100 text-gray-600 border-gray-200',
              };
              const Icon = meta.icon;
              const primary = (a.guardians ?? []).find((g) => g.isPrimaryContact);
              return (
                <button
                  key={a.id}
                  onClick={() => onOpenApplication(a.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {a.firstName} {a.surname}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}
                      >
                        <Icon size={10} />
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <GraduationCap size={12} />
                        Form {a.applyingForForm} ({a.applyingForYear})
                      </span>
                      <span className="font-mono">{a.referenceNumber}</span>
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {a.submittedAt.slice(0, 10)}
                      </span>
                    </div>
                    {primary && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        Primary: {primary.fullName}
                        {primary.email && ` · ${primary.email}`}
                      </p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-gray-400 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </SoftCard>
    </div>
  );
}
